import { FC, useMemo } from 'react';
import { KidTemplateType } from '../../../../../api/kids/types';
import {
  getTemplateReferenceFieldLabel,
  inputClassName,
  formDropdownWidthClassName,
} from '../templateManagement.constants';
import { TemplateReferenceOptions } from '../templateManagement.types';
import { formatFundOptionLabel } from '../templateManagement.utils';
import FormRow from './FormRow';
import TemplateFormDropdown from './TemplateFormDropdown';

const ENTITY_TEMPLATE_TYPES = new Set<KidTemplateType>([
  KidTemplateType.SUB_FUND,
  KidTemplateType.FUND_MANAGER,
  KidTemplateType.CIU_FUND,
  KidTemplateType.ADMINISTRATOR,
]);

const TemplateReferenceField: FC<
  TemplateReferenceOptions & {
    label?: string;
    templateType: KidTemplateType;
    value: string;
    onChange: (value: string) => void;
    layout?: 'inline' | 'stacked';
    /** Render only the input control (no FormRow wrapper). */
    controlOnly?: boolean;
    dropdownWidthClassName?: string;
  }
> = ({
  label,
  templateType,
  value,
  onChange,
  layout = 'inline',
  controlOnly = false,
  dropdownWidthClassName = formDropdownWidthClassName,
  availableFunds,
  availableFundManagers,
  availableCIUs,
  availableAdministrators,
}) => {
  const fieldLabel = label ?? getTemplateReferenceFieldLabel(templateType);

  const { options, placeholder } = useMemo(() => {
    switch (templateType) {
      case KidTemplateType.SUB_FUND:
        return {
          placeholder: 'Select fund',
          options: availableFunds.map((fund) => ({
            id: fund.fund_id,
            label: formatFundOptionLabel(fund),
          })),
        };
      case KidTemplateType.FUND_MANAGER:
        return {
          placeholder: 'Select fund manager',
          options: availableFundManagers.map((fundManager) => ({
            id: fundManager.fund_manager_id,
            label: fundManager.fund_manager_name,
          })),
        };
      case KidTemplateType.CIU_FUND:
        return {
          placeholder: 'Select CIU',
          options: availableCIUs.map((ciu) => ({
            id: ciu.ciu_id,
            label: ciu.ciu_name,
          })),
        };
      case KidTemplateType.ADMINISTRATOR:
        return {
          placeholder: 'Select administrator',
          options: availableAdministrators.map((administrator) => ({
            id: administrator.administrator_id,
            label: administrator.administrator_name,
          })),
        };
      default:
        return { placeholder: fieldLabel, options: [] };
    }
  }, [
    availableAdministrators,
    availableCIUs,
    availableFundManagers,
    availableFunds,
    fieldLabel,
    templateType,
  ]);

  if (templateType === KidTemplateType.MANCO) return null;

  const control = ENTITY_TEMPLATE_TYPES.has(templateType) ? (
    <TemplateFormDropdown
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      searchBar
      widthClassName={dropdownWidthClassName}
    />
  ) : (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={fieldLabel}
      className={inputClassName}
    />
  );

  if (controlOnly) {
    return control;
  }

  if (ENTITY_TEMPLATE_TYPES.has(templateType)) {
    return (
      <FormRow label={fieldLabel} layout={layout}>
        {control}
      </FormRow>
    );
  }

  return (
    <FormRow label={fieldLabel} layout={layout}>
      {control}
    </FormRow>
  );
};

export default TemplateReferenceField;
